<script lang="ts">
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import type {
    SideBySideStoryblok,
    SideBySideWithTimerStoryblok,
  } from "$types/components";
  import StoryblokComponent from "$lib/StoryblokComponent.svelte";
  import { textDir } from "$stores";
  import Image from "$lib/atoms/Image.svelte";
  import TimerSlider from "$lib/molecules/TimerSlider.svelte";
  import TiltedGallery from "$lib/molecules/TiltedGallery.svelte";
  import Section from "$lib/molecules/Section.svelte";
  import VideoV2 from "$lib/molecules/VideoV2.svelte";

  export let blok: SideBySideStoryblok | SideBySideWithTimerStoryblok;

  // Side by side module can be part of a bigger module. In this case some new style rules are applied
  export let encapsulated = false;

  const backgroundColor: string =
    blok.backgroundColor && blok.backgroundColor.length > 0
      ? blok.backgroundColor[0].color
      : "";
  const isThemeLight =
    backgroundColor === "blue" || backgroundColor === "green";
  let sectionClass = "side-by-side";
  if (encapsulated) sectionClass += " encapsulated";

  if (
    blok.media &&
    blok.media.length > 0 &&
    blok.media[0].component === "tiltedGallery"
  )
    sectionClass += " has-slider";
  let section;
</script>

<Section
  bind:this={section}
  {blok}
  class="{sectionClass} {isThemeLight ? 'is-light' : ''}"
>
  {#if !blok.isCompact}
    <GridContainer>
      <GridItem col={12} colMd={9}>
        <div class="section-header" dir={$textDir}>
          <div class="left-part">
            {#if blok.eyebrow && blok.eyebrow !== ""}
              <p class="eyebrow type:section-title">{blok.eyebrow}</p>
            {/if}
            {#if encapsulated}
              <h3 class="type:h3">{@html blok.headline}</h3>
            {:else}
              <svelte:element
                this={blok.headlineLevel === "h1" ? "h1" : "h2"}
                class="type:h2">{@html blok.headline}</svelte:element
              >
            {/if}
          </div>
        </div>
      </GridItem>
    </GridContainer>
  {/if}

  {#if blok.content && blok.content.length > 0}
    <GridContainer
      alignItems={blok.verticalAlignment === "top" ? "flex-start" : "center"}
      direction={encapsulated ? "column-reverse" : "row"}
      directionMd={blok.mediaOnTheLeft ? "row-reverse" : "row"}
    >
      <GridItem
        col={12}
        colMd={5}
        offset={0}
        offsetMd={blok.mediaOnTheLeft ? 1 : 0}
      >
        {#if blok.isCompact}
          <div
            class="section-header compact {isThemeLight ? 'is-light' : ''}"
            dir={$textDir}
          >
            {#if blok.eyebrow && blok.eyebrow !== ""}
              <p class="eyebrow type:section-title">{blok.eyebrow}</p>
            {/if}
            {#if encapsulated}
              <h3 class="type:h3">{@html blok.headline}</h3>
            {:else}
              <svelte:element
                this={blok.headlineLevel === "h1" ? "h1" : "h2"}
                class="type:h2">{@html blok.headline}</svelte:element
              >
            {/if}
          </div>
        {/if}

        <div class="content">
          {#each blok.content as content}
            <StoryblokComponent blok={content} isLight={isThemeLight} />
          {/each}
        </div>
      </GridItem>
      <GridItem
        col={12}
        colMd={6}
        offset={0}
        offsetMd={blok.mediaOnTheLeft ? 0 : 1}
      >
        <div class="media md:pt-[12px]">
          {#if blok.media.length > 0}
            {#if blok.media[0].component === "image"}
              <Image
                media={blok.media[0].object}
                aspect={!blok.disableAspectRatio ? "4-5" : null}
                caption={blok.media[0].caption[0]}
                lgSize="50"
                smSize="100"
              />
            {:else if blok.media[0].component === "video"}
              <VideoV2 blok={blok.media[0]} />
            {:else if blok.media[0].component === "tiltedGallery"}
              <TiltedGallery
                blok={blok.media}
                {backgroundColor}
                class="side-gallery"
              />
            {/if}
          {/if}
        </div>
      </GridItem>
    </GridContainer>
  {/if}

  {#if blok.slider}
    <TimerSlider blok={blok.slider[0]} mediaOnTheLeft={blok.mediaOnTheLeft}>
      {#if blok.isCompact}
        <div class="section-header compact">
          <p class="eyebrow type:section-title">{blok.eyebrow}</p>
          <svelte:element
            this={blok.headlineLevel === "h1" ? "h1" : "h2"}
            class="type:h2">{@html blok.headline}</svelte:element
          >
        </div>
      {/if}
    </TimerSlider>
  {/if}
</Section>

<style lang="scss">
  :global(.encapsulated) {
    .media {
      margin-top: 0;
      margin-bottom: 40px;

      @include breakpoint("small") {
        margin-bottom: 0;
      }
    }
  }

  h2 {
    margin-bottom: fluid(80px, 32px);
  }

  .compact {
    h2 {
      margin-bottom: fluid(32px, 32px);
    }

    h3 {
      margin-bottom: fluid(56px, 32px);
    }
  }

  .content {
    :global(> *) {
      margin-bottom: 24px;
    }

    :global(> *:last-child) {
      margin-bottom: 0;
    }

    :global(.labelled-text + .button) {
      margin-top: fluid(56px, 40px);
    }
  }

  .media {
    margin-top: 56px;

    @include breakpoint("small") {
      margin-top: 0;
    }
  }

  :global(.has-background .labelled-text) {
    margin-bottom: fluid(16px, 16px);
  }

  :global(.side-gallery) {
    top: 0;

    @include breakpoint("small") {
      top: fluid(-28px, -28px);
    }
  }
</style>
